import clsx from "clsx";
import TextInput from "../../components/TextInput/TextInput";
import { useTranslation } from "react-i18next";
import CustomSelect from "../../components/Select/CustomSelect";
import { ICON_SIZES, ETransactionType } from "../../helpers/constants";
import CollapsibleBlock from "../../components/CollapsibleBlock/CollapsibleBlock";
import useCollapsibles, {
  ETransactionPageMobileCollapsibles,
} from "../../hooks/useCollapsibles";
import useMobile from "../../hooks/useMobile";
import { useCallback, useEffect, useMemo } from "react";
import { MdDoneAll, MdOutlinePriorityHigh } from "react-icons/md";
import useAllTransactions from "src/hooks/useAllTransactions";

type TransactionHeaderType = {
  clientProvider: string;
  onClientProviderChange: (id: string) => void;
  date: string;
  onDateChange: (date: string) => void;
  documentNumber: string;
  onDocumentNumberChange: (document: string) => void;
  transactionType: ETransactionType;
  onDescriptionChange: (value: string) => void;
  description: string;
  onCategoryChange: (value: string) => void;
  category: string;
  details: Array<any>;
  onMethodChange: (value: string) => void;
  isNewTransaction: boolean;
};

const TransactionHeader: React.FC<TransactionHeaderType> = ({
  clientProvider,
  onClientProviderChange,
  date,
  onDateChange,
  documentNumber,
  onDocumentNumberChange,
  transactionType,
  onDescriptionChange,
  description,
  onCategoryChange,
  category,
  details = [],
  onMethodChange,
  isNewTransaction = true,
}) => {
  const {
    collapsiblePreferences,
    updateCollapsible,
    isTransactionPageMobileCollapsibleOpen,
    toggleTransactionPageMobileCollapsible,
  } = useCollapsibles();
  const { allTransactions, isLoadingAllTransactions } = useAllTransactions();

  const { t } = useTranslation();

  const { allCategoriesObject, allSourcesObject, allDescriptionsObject } =
    useAllTransactions();
  const { isMobile } = useMobile();
  const isDetailedTransaction: boolean = !!details?.length;
  const headerDataIsValid = useMemo(() => {
    return (
      !!date &&
      !!clientProvider &&
      (isDetailedTransaction ? true : !!description) &&
      !!category
    );
  }, [date, clientProvider, description, isDetailedTransaction, category]);

  const clientProviderLatestTransactions = useCallback(() => {
    if (!isLoadingAllTransactions && clientProvider) {
      const lastTransactions = allTransactions?.filter?.(
        (transaction: any) => transaction?.payTo === clientProvider
      );

      if (lastTransactions?.length) {
        const [lastOne] = lastTransactions;

        onDescriptionChange(lastOne?.description || "");
        onCategoryChange(lastOne?.category || "");
        onMethodChange(lastOne.paymentMethod);
      }
    }
  }, [allTransactions, clientProvider, isLoadingAllTransactions]);

  useEffect(() => {
    if (isNewTransaction) {
      clientProviderLatestTransactions();
    }
  }, [clientProvider, isNewTransaction]);

  return (
    <CollapsibleBlock
      icon={
        headerDataIsValid ? (
          <MdDoneAll size={ICON_SIZES.SM} className="text-lime-400" />
        ) : (
          <MdOutlinePriorityHigh
            size={ICON_SIZES.SM}
            className="text-red-500 dark:text-rose-400/80"
          />
        )
      }
      title="Encabezado"
      badge={headerDataIsValid ? date : "Incompleto"}
      badgeKind={headerDataIsValid ? "info" : "danger"}
      uppercase
      open={
        isMobile
          ? isTransactionPageMobileCollapsibleOpen(
              ETransactionPageMobileCollapsibles.HEADER
            )
          : collapsiblePreferences.transactionHeaderOpen
      }
      onChange={(isOpen: boolean) => {
        if (isMobile) {
          toggleTransactionPageMobileCollapsible(
            ETransactionPageMobileCollapsibles.HEADER
          );
        } else {
          updateCollapsible({ transactionHeaderOpen: isOpen });
        }
      }}
    >
      <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
        <div
          className={clsx([
            "flex",
            "flex-col",
            "lg:flex-row",
            "gap-2",
            "md:gap-4",
            "max-w-full",
            "w-full",
          ])}
        >
          <div className={clsx(["flex", "flex-row", "gap-4", "md:w-1/2"])}>
            <div className={clsx(["flex", "sflex-shrink", "w-1/2 md:w-full"])}>
              <TextInput
                placeholder={t("transaction_page.date_placeholder")}
                value={date}
                onChange={(e) => onDateChange(e?.target?.value)}
                label={t("transaction_page.date_label")}
                type="date"
                required
              />
            </div>
            <div className={clsx(["flex", "sflex-shrink", "w-1/2 md:w-full"])}>
              <TextInput
                placeholder={t("transaction_page.document_number_placeholder")}
                value={documentNumber}
                onChange={(e) => onDocumentNumberChange(e?.target?.value)}
                label={t("transaction_page.document_number_label")}
                type="text"
              />
            </div>
          </div>
          <div
            className={clsx([
              "flex",
              "flex-shrink",
              "items-end",
              "lg:w-1/2",
              "gap-2",
            ])}
          >
            <div className={clsx(["flex", "w-full", "flex-col", "gap-1"])}>
              <CustomSelect
                creatable
                required
                value={clientProvider}
                onChange={(e: any) => onClientProviderChange(e?.value)}
                options={allSourcesObject}
                placeholder={
                  transactionType === ETransactionType.INCOME
                    ? "Nombre del origen"
                    : "Nombre del destino"
                }
                label={t(
                  transactionType === ETransactionType.INCOME
                    ? "Origen"
                    : "Destino"
                )}
                allowSearchModal
              />
            </div>
          </div>
        </div>
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "md:flex-row",
            "md:w-full",
            "gap-2",
            "md:gap-4",
          ])}
        >
          <div
            className={clsx([
              "flex",
              "flex-row",
              "gap-2",
              "items-end",
              "lg:w-1/2",
              "w-full",
            ])}
          >
            <CustomSelect
              onChange={(e: any) => onDescriptionChange(e?.value)}
              value={description}
              label={t("Descripción")}
              placeholder={t(
                "transaction_page.transaction_description_placeholder"
              )}
              required={!isDetailedTransaction}
              options={allDescriptionsObject}
              creatable
              allowSearchModal
            />
          </div>
          <div className="flex flex-row gap-2 w-full lg:w-1/2">
            <CustomSelect
              onChange={(e: any) => onCategoryChange(e?.value)}
              value={category}
              placeholder={t("transaction_page.category_placeholder")}
              label={t("transaction_page.category_label")}
              required
              options={allCategoriesObject}
              creatable
              allowSearchModal
            />
          </div>
        </div>
      </div>
    </CollapsibleBlock>
  );
};

export default TransactionHeader;
